import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api.config';
import SearchableDropdown from './SearchableDropdown';
import { nationalities } from '../constants/nationalities';

const CreateInvestorModal = ({ isOpen, onClose, onSuccess, initialType = 'INDIVIDUAL', initialData = null, isEdit = false, disableNetworkType = false }) => {
    const [formData, setFormData] = useState({
        investorType: 'INDIVIDUAL', // INDIVIDUAL or NETWORK
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        additionalPhoneNumber: '',
        organizationName: '',
        organizationType: '',
        investmentFocus: '',
        website: '',
        investorNetworkId: '',
        registrationType: 'PAN',
        registrationNumber: '',
        physicalAddress: '',
        nationality: '',
        aadharNumber: '',
        panNumber: '',
        dinNumber: ''
    });
    const [networks, setNetworks] = useState([]);
    const [organizationTypes, setOrganizationTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [networkSearch, setNetworkSearch] = useState('');
    const [errors, setErrors] = useState({});

    const [investmentFocusOptions, setInvestmentFocusOptions] = useState([]);

    useEffect(() => {
        if (isOpen) {
            if (isEdit && initialData) {
                setFormData({
                    investorType: initialType,
                    name: initialData.name || '',
                    email: initialData.email || '',
                    password: '',
                    phoneNumber: initialData.phoneNumber || '',
                    additionalPhoneNumber: initialData.additionalPhoneNumber || '',
                    organizationName: initialData.organizationName || '',
                    organizationType: initialData.organizationType || '',
                    investmentFocus: initialData.department || '', // mapping department -> investmentFocus
                    website: initialData.website || '',
                    investorNetworkId: initialData.investorNetworkId || '',
                    registrationType: initialType === 'NETWORK' ? (initialData.investorDetails?.registrationType || 'PAN') : 'PAN',
                    registrationNumber: initialType === 'NETWORK' ? (initialData.investorDetails?.registrationNumber || '') : '',
                    physicalAddress: initialData.investorDetails?.physicalAddress || '',
                    nationality: initialData.investorDetails?.nationality || '',
                    aadharNumber: initialData.investorDetails?.aadharNumber || '',
                    panNumber: initialData.investorDetails?.panNumber || '',
                    dinNumber: initialData.investorDetails?.dinNumber || ''
                });
            } else {
                // Reset form completely for create mode, but preserve investorNetworkId and email if provided (e.g. from search)
                setFormData({
                    investorType: initialType,
                    name: initialData?.name || '',
                    email: initialData?.email || '',
                    password: '',
                    phoneNumber: initialData?.phoneNumber || '',
                    additionalPhoneNumber: '',
                    organizationName: '',
                    organizationType: '',
                    investmentFocus: '',
                    website: '',
                    investorNetworkId: initialData?.investorNetworkId || '',
                    registrationType: 'PAN',
                    registrationNumber: '',
                    physicalAddress: '',
                    nationality: '',
                    aadharNumber: '',
                    panNumber: '',
                    dinNumber: ''
                });
            }
            fetchNetworks();
            fetchSettings();
        }
    }, [isOpen, initialType, initialData, isEdit]);

    const fetchNetworks = async () => {
        try {
            const response = await fetch(getApiUrl('admin/users/investor-networks'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            setNetworks(data);
        } catch (error) {
            console.error('Failed to fetch networks:', error);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await fetch(getApiUrl('settings/grouped'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();

            if (data.ORGANIZATION_TYPE) {
                setOrganizationTypes(data.ORGANIZATION_TYPE);
            }

            if (data.INDUSTRY) {
                setInvestmentFocusOptions(data.INDUSTRY);
            }
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            investorType: type,
            // Reset type-specific fields
            organizationName: type === 'NETWORK' ? prev.organizationName : '',
            organizationType: type === 'NETWORK' ? prev.organizationType : '',
            investmentFocus: type === 'NETWORK' ? prev.investmentFocus : '',
            website: type === 'NETWORK' ? prev.website : '',
            registrationType: type === 'NETWORK' ? (prev.registrationType || 'PAN') : 'PAN',
            registrationNumber: type === 'NETWORK' ? prev.registrationNumber : '',
            investorNetworkId: type === 'INDIVIDUAL' ? prev.investorNetworkId : '',
                    physicalAddress: type === 'INDIVIDUAL' ? prev.physicalAddress : '',
                    nationality: type === 'INDIVIDUAL' ? prev.nationality : '',
                    aadharNumber: prev.aadharNumber,
                    panNumber: prev.panNumber,
                    dinNumber: prev.dinNumber
                }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate mandatory fields for individual investors
        if (formData.investorType === 'INDIVIDUAL' && !formData.nationality?.trim()) {
            setErrors(prev => ({ ...prev, nationality: 'Nationality is required.' }));
            return;
        }

        setLoading(true);

        try {
            const payload = {
                name: formData.name,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                additionalPhoneNumber: formData.additionalPhoneNumber,
                role: formData.investorType === 'NETWORK' ? 'INVESTOR_NETWORK' : 'INVESTOR'
            };

            if (!isEdit && formData.password) {
                payload.password = formData.password;
            }

            if (formData.investorType === 'NETWORK') {
                payload.organizationName = formData.organizationName;
                payload.organizationType = formData.organizationType;
                payload.department = formData.investmentFocus; // Using department field for investment focus
                // For investor networks, use registration type/number instead of PAN/Aadhaar
                payload.registrationType = formData.registrationType;
                payload.registrationNumber = formData.registrationNumber?.trim() || '';
            } else {
                if (formData.investorNetworkId) {
                    payload.investorNetworkId = formData.investorNetworkId;
                }
                // Add individual investor specific details
                payload.physicalAddress = formData.physicalAddress;
                payload.nationality = formData.nationality;
                // Add Aadhar and PAN for individuals (mandatory fields)
                payload.aadharNumber = formData.aadharNumber;
                payload.panNumber = formData.panNumber;
                if (formData.dinNumber?.trim()) payload.dinNumber = formData.dinNumber.trim();
            }

            let response;
            if (isEdit) {
                response = await fetch(getApiUrl(`admin/users/${initialData.id}`), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await fetch(getApiUrl('admin/users/create'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (response.ok) {
                onSuccess();
                handleClose();
            } else {
                const errorText = await response.text();
                let errorMessage = errorText;

                // If it's a JSON error object, try to parse message
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.message || errorText;

                    // Handle field-specific validation errors
                    if (errorJson.validationErrors) {
                        setErrors(errorJson.validationErrors);
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    // Use raw text if not JSON
                }

                // Parse error message to identify specific fields (fallback for legacy errors)
                const newErrors = {};
                if (errorMessage.toLowerCase().includes('aadhaar') || errorMessage.toLowerCase().includes('aadhar')) {
                    newErrors.aadharNumber = 'This Aadhaar number is already registered with another investor.';
                } else if (errorMessage.toLowerCase().includes('pan')) {
                    newErrors.panNumber = 'This PAN number is already linked to an account.';
                } else {
                    newErrors.general = errorMessage;
                }

                setErrors(newErrors);
                console.error(`Failed to ${isEdit ? 'update' : 'create'} investor:`, errorMessage);
            }
        } catch (error) {
            console.error('Error creating investor:', error);
            // Note: Parent component should handle alerts via onError callback
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            investorType: 'INDIVIDUAL',
            name: '',
            email: '',
            password: '',
            phoneNumber: '',
            additionalPhoneNumber: '',
            organizationName: '',
            organizationType: '',
            investmentFocus: '',
            website: '',
            investorNetworkId: '',
            registrationType: 'PAN',
            registrationNumber: '',
            physicalAddress: '',
            nationality: '',
            aadharNumber: '',
            panNumber: '',
            dinNumber: ''
        });
        setNetworkSearch('');
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-lg">
                    <h2 className="text-xl font-bold">
                        {isEdit ? 'Edit Investor' : (disableNetworkType ? 'Add Investor' : 'Create Investor')}
                    </h2>
                    <p className="text-blue-100 text-sm mt-1">
                        {isEdit ? 'Update investor details' : (disableNetworkType ? 'Add a new investor to this network' : 'Add a new investor network or individual investor')}
                    </p>
                </div>

                {errors.general && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{errors.general}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Investor Type Selector */}
                    {/* Investor Type Selector */}
                    {/* Investor Type Selector */}
                    {!disableNetworkType && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Investor Type *</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('INDIVIDUAL')}
                                    className={`p-4 border-2 rounded-lg text-left transition ${formData.investorType === 'INDIVIDUAL'
                                        ? 'border-blue-600 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.investorType === 'INDIVIDUAL' ? 'border-blue-600' : 'border-gray-300'
                                            }`}>
                                            {formData.investorType === 'INDIVIDUAL' && (
                                                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">Individual Investor</p>
                                            <p className="text-xs text-gray-500">Person working at a network</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('NETWORK')}
                                    className={`p-4 border-2 rounded-lg text-left transition ${formData.investorType === 'NETWORK'
                                        ? 'border-blue-600 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.investorType === 'NETWORK' ? 'border-blue-600' : 'border-gray-300'
                                            }`}>
                                            {formData.investorType === 'NETWORK' && (
                                                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">Investor Network</p>
                                            <p className="text-xs text-gray-500">VC firm, angel network, etc.</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Common Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {formData.investorType === 'NETWORK' ? 'Contact Person Name *' : 'Full Name *'}
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={isEdit}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                placeholder={formData.investorType === 'NETWORK' ? 'Contact person' : 'Investor name'}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                disabled={isEdit}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                placeholder="email@example.com"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {!isEdit && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="••••••••"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="+1 234 567 8900"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Phone</label>
                            <input
                                type="tel"
                                name="additionalPhoneNumber"
                                value={formData.additionalPhoneNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="+1 234 567 8900"
                            />
                        </div>
                    </div>

                    {/* Network-specific Fields */}
                    {formData.investorType === 'NETWORK' && (
                        <>
                            <div className="border-t pt-4">
                                <h3 className="text-sm font-semibold text-gray-900 mb-3">Network Information</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                                        <input
                                            type="text"
                                            name="organizationName"
                                            value={formData.organizationName}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., Sequoia Capital"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type *</label>
                                            <select
                                                name="organizationType"
                                                value={formData.organizationType}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">Select Type</option>
                                                {organizationTypes.map((type, index) => (
                                                    <option key={index} value={type.value}>
                                                        {type.label || type.value}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Focus</label>
                                            <select
                                                name="investmentFocus"
                                                value={formData.investmentFocus}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">Select Investment Focus</option>
                                                {investmentFocusOptions.length > 0 ? (
                                                    investmentFocusOptions.map((option) => (
                                                        <option key={option.id} value={option.value}>
                                                            {option.value}
                                                        </option>
                                                    ))
                                                ) : (
                                                    // Fallback options if fetch fails
                                                    <>
                                                        <option value="SaaS">SaaS (Software as a Service)</option>
                                                        <option value="FinTech">FinTech (Financial Technology)</option>
                                                        <option value="HealthTech">HealthTech (Healthcare Technology)</option>
                                                        <option value="EdTech">EdTech (Education Technology)</option>
                                                        <option value="E-Commerce">E-Commerce</option>
                                                        <option value="AI/ML">AI/ML</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                                        <input
                                            type="url"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="https://example.com"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Registration Type *
                                            </label>
                                            <select
                                                name="registrationType"
                                                value={formData.registrationType}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="PAN">PAN</option>
                                                <option value="GST">GST</option>
                                                <option value="CIN">CIN</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Registration Number *
                                            </label>
                                            <input
                                                type="text"
                                                name="registrationNumber"
                                                value={formData.registrationNumber}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                                placeholder={
                                                    formData.registrationType === 'GST'
                                                        ? '27AABCU9603R1ZM'
                                                        : formData.registrationType === 'CIN'
                                                            ? 'U74999MH2014PTC123456'
                                                            : 'ABCDE1234F'
                                                }
                                            />
                                            {errors.registrationNumber && (
                                                <p className="text-red-500 text-xs mt-1">{errors.registrationNumber}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Individual-specific Fields */}
                    {formData.investorType === 'INDIVIDUAL' && (
                        <div className="border-t pt-4">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Investor Details</h3>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nationality *</label>
                                    <SearchableDropdown
                                        name="nationality"
                                        value={formData.nationality}
                                        onChange={handleChange}
                                        options={nationalities}
                                        placeholder="Select Nationality"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.nationality ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                    />
                                    {errors.nationality && (
                                        <p className="text-red-500 text-xs mt-1">{errors.nationality}</p>
                                    )}
                                </div>
                                {!disableNetworkType && (
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Investor Network</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={networkSearch}
                                                onChange={(e) => setNetworkSearch(e.target.value)}
                                                onFocus={() => setNetworkSearch('')}
                                                placeholder="Search networks..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                            <svg className="absolute right-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>

                                        {/* Display selected network */}
                                        {formData.investorNetworkId && !networkSearch && (
                                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                                                <span className="text-sm text-blue-900">
                                                    {networks.find(n => n.id === parseInt(formData.investorNetworkId))?.organizationName ||
                                                        networks.find(n => n.id === parseInt(formData.investorNetworkId))?.name ||
                                                        'Selected Network'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, investorNetworkId: '' })}
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}

                                        {/* Filtered dropdown */}
                                        {networkSearch && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {networks
                                                    .filter(network => {
                                                        const searchLower = networkSearch.toLowerCase();
                                                        const name = (network.organizationName || network.name || '').toLowerCase();
                                                        return name.includes(searchLower);
                                                    })
                                                    .map((network) => (
                                                        <button
                                                            key={network.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setFormData({ ...formData, investorNetworkId: network.id });
                                                                setNetworkSearch('');
                                                            }}
                                                            className="w-full px-4 py-2 text-left hover:bg-blue-50 transition text-sm"
                                                        >
                                                            <div className="font-medium text-gray-900">
                                                                {network.organizationName || network.name}
                                                            </div>
                                                            {network.organizationType && (
                                                                <div className="text-xs text-gray-500">
                                                                    {network.organizationType}
                                                                </div>
                                                            )}
                                                        </button>
                                                    ))}
                                                {networks.filter(network => {
                                                    const searchLower = networkSearch.toLowerCase();
                                                    const name = (network.organizationName || network.name || '').toLowerCase();
                                                    return name.includes(searchLower);
                                                }).length === 0 && (
                                                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                            No networks found
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                                <textarea
                                    name="physicalAddress"
                                    value={formData.physicalAddress}
                                    onChange={handleChange}
                                    rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter physical/residential address"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Aadhar Number *
                                        <span className="text-xs text-gray-500 ml-1">(12 digits)</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="aadharNumber"
                                        value={formData.aadharNumber}
                                        onChange={handleChange}
                                        required
                                        maxLength="12"
                                        pattern="[0-9]{12}"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.aadharNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                            }`}
                                        placeholder="XXXX XXXX XXXX"
                                    />
                                    {errors.aadharNumber && (
                                        <p className="text-red-500 text-xs mt-1">{errors.aadharNumber}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        PAN Number *
                                        <span className="text-xs text-gray-500 ml-1">(10 characters)</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="panNumber"
                                        value={formData.panNumber}
                                        onChange={handleChange}
                                        required
                                        maxLength="10"
                                        pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase ${errors.panNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                            }`}
                                        placeholder="ABCDE1234F"
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                    {errors.panNumber && (
                                        <p className="text-red-500 text-xs mt-1">{errors.panNumber}</p>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    DIN (Director Identification Number)
                                    <span className="text-xs text-gray-500 ml-1">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    name="dinNumber"
                                    value={formData.dinNumber}
                                    onChange={handleChange}
                                    maxLength="20"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g. 12345678"
                                />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50"
                        >
                            {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update' : `Create ${formData.investorType === 'NETWORK' ? 'Network' : 'Investor'}`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateInvestorModal;
